/**
 * @fileoverview Silk Background Effect Component
 * 
 * @description
 * A WebGL-based animated silk/fabric effect background using Three.js and React Three Fiber.
 * Creates smooth, flowing visual patterns with customizable colors and animation parameters.
 * 
 * Based on React Bits Silk component.
 * Replaces the previous LiquidEther background effect.
 * 
 * @component Silk
 * @version 1.0.0
 * 
 * @example
 * <Silk
 *   speed={5}
 *   scale={1}
 *   color="#7B7481"
 *   noiseIntensity={1.5}
 *   rotation={0}
 * />
 */

/* eslint-disable react/no-unknown-property */
import React from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { forwardRef, useRef, useMemo, useLayoutEffect } from 'react';
import { Color } from 'three';

/**
 * Lightweight error boundary that silently swallows WebGL / Three.js failures
 * so the rest of the app keeps working (iOS Safari can lose WebGL contexts on refresh).
 */
class SilkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(err) {
    console.warn('[Silk] WebGL/Three.js error suppressed:', err.message);
  }
  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

function isWebGLAvailable() {
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') || c.getContext('webgl'));
  } catch {
    return false;
  }
}

/**
 * Converts a hex color string to normalized RGB values (0-1 range)
 * @param {string} hex - Hex color string (e.g., '#7B7481')
 * @returns {number[]} Array of [r, g, b] values normalized to 0-1
 */
const hexToNormalizedRGB = hex => {
  hex = hex.replace('#', '');
  return [
    parseInt(hex.slice(0, 2), 16) / 255,
    parseInt(hex.slice(2, 4), 16) / 255,
    parseInt(hex.slice(4, 6), 16) / 255
  ];
};

/**
 * Vertex shader for the silk effect
 * Passes UV coordinates and position to fragment shader
 */
const vertexShader = `
varying vec2 vUv;
varying vec3 vPosition;

void main() {
  vPosition = position;
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

/**
 * Fragment shader for the silk effect
 * Creates animated flowing patterns with noise
 */
const fragmentShader = `
varying vec2 vUv;
varying vec3 vPosition;

uniform float uTime;
uniform vec3  uColor;
uniform float uSpeed;
uniform float uScale;
uniform float uRotation;
uniform float uNoiseIntensity;

const float e = 2.71828182845904523536;

float noise(vec2 texCoord) {
  float G = e;
  vec2  r = (G * sin(G * texCoord));
  return fract(r.x * r.y * (1.0 + texCoord.x));
}

vec2 rotateUvs(vec2 uv, float angle) {
  float c = cos(angle);
  float s = sin(angle);
  mat2  rot = mat2(c, -s, s, c);
  return rot * uv;
}

void main() {
  float rnd        = noise(gl_FragCoord.xy);
  vec2  uv         = rotateUvs(vUv * uScale, uRotation);
  vec2  tex        = uv * uScale;
  float tOffset    = uSpeed * uTime;

  tex.y += 0.03 * sin(8.0 * tex.x - tOffset);

  float pattern = 0.6 +
                  0.4 * sin(5.0 * (tex.x + tex.y +
                                   cos(3.0 * tex.x + 5.0 * tex.y) +
                                   0.02 * tOffset) +
                           sin(20.0 * (tex.x + tex.y - 0.1 * tOffset)));

  vec4 col = vec4(uColor, 1.0) * vec4(pattern) - rnd / 15.0 * uNoiseIntensity;
  col.a = 1.0;
  gl_FragColor = col;
}
`;

/**
 * SilkPlane Component
 * Internal component that renders the animated plane with shader material
 */
const SilkPlane = forwardRef(function SilkPlane({ uniforms }, ref) {
  const { viewport } = useThree();

  useLayoutEffect(() => {
    if (ref.current) {
      ref.current.scale.set(viewport.width, viewport.height, 1);
    }
  }, [ref, viewport]);

  useFrame((_, delta) => {
    if (!ref.current?.material?.uniforms?.uTime) return;
    ref.current.material.uniforms.uTime.value += 0.1 * delta;
  });

  return (
    <mesh ref={ref}>
      <planeGeometry args={[1, 1, 1, 1]} />
      <shaderMaterial uniforms={uniforms} vertexShader={vertexShader} fragmentShader={fragmentShader} />
    </mesh>
  );
});
SilkPlane.displayName = 'SilkPlane';

/**
 * Silk Component
 * Main export - renders an animated silk/fabric background effect
 * 
 * @param {Object} props - Component props
 * @param {number} [props.speed=5] - Animation speed multiplier
 * @param {number} [props.scale=1] - Scale of the pattern
 * @param {string} [props.color='#7B7481'] - Base color in hex format
 * @param {number} [props.noiseIntensity=1.5] - Intensity of the noise effect
 * @param {number} [props.rotation=0] - Rotation angle of the pattern in radians
 */
const SilkInner = ({ speed, scale, color, noiseIntensity, rotation }) => {
  const meshRef = useRef();

  const uniforms = useMemo(
    () => ({
      uSpeed: { value: speed },
      uScale: { value: scale },
      uNoiseIntensity: { value: noiseIntensity },
      uColor: { value: new Color(...hexToNormalizedRGB(color)) },
      uRotation: { value: rotation },
      uTime: { value: 0 }
    }),
    [speed, scale, noiseIntensity, color, rotation]
  );

  return (
    <Canvas dpr={[1, 2]} frameloop="always">
      <SilkPlane ref={meshRef} uniforms={uniforms} />
    </Canvas>
  );
};

const Silk = ({
  speed = 5,
  scale = 1,
  color = '#7B7481',
  noiseIntensity = 1.5,
  rotation = 0
}) => {
  if (!isWebGLAvailable()) return null;

  return (
    <SilkErrorBoundary>
      <SilkInner
        speed={speed}
        scale={scale}
        color={color}
        noiseIntensity={noiseIntensity}
        rotation={rotation}
      />
    </SilkErrorBoundary>
  );
};

export default Silk;
